import React, { useState, useEffect, useMemo } from 'react';
import {
  Container, Typography, Box, IconButton, Button, List, ListItemButton,
  ListItemText, CircularProgress, Alert,
  Chip, ListItemAvatar, Avatar, Divider, FormControlLabel, Switch,
  useTheme, useMediaQuery, Dialog, DialogTitle, DialogContent, DialogContentText,
  DialogActions, Fab, Snackbar,
} from '@mui/material';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AddIcon from '@mui/icons-material/Add';
import { apiFetch } from '../apiFetch';
import { Store } from '../types/Store';
import { ShoppingList } from '../types/ShoppingList';
import { ShoppingListGrocery } from '../types/ShoppingListGrocery';
import { Price } from '../types/Price';
import ShoppingListItemsPreviewModal from '../modals/ShoppingListItemsPreviewModal';
import ShoppingListFormModal from '../modals/ShoppingListFormModal';
import EnterPriceModal from '../modals/EnterPriceModal';
import PartialPickModal from '../modals/PartialPickModal';
import AddGroceryWhileShoppingModal, { AddGroceryResult } from '../modals/AddGroceryWhileShopping';
import ReceiptScanModal from '../modals/ReceiptScanModal';
import PausedShoppingTripsModal from '../modals/PausedShoppingTripsModal';
import ShoppingListItem from '../components/ShoppingListItem';
import StepWizard from '../components/StepWizard';
import { haptics } from '../utils/haptics';
import { useShoppingMode } from '../components/ShoppingModeContext';
import { analytics } from '../services/analytics';
import { useContext } from 'react';
import { TutorialContext } from '../components/TutorialProvider';

const steps = ['Select Store', 'Select Shopping List', 'Go Shopping'];

const GoingShopping: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { shoppingMode, startShopping, endShopping, pauseShopping } = useShoppingMode();
  const [activeStep, setActiveStep] = useState(0);
  const [restoringSession, setRestoringSession] = useState(true);

  // Store selection
  const [stores, setStores] = useState<Store[]>([]);
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [storesLoading, setStoresLoading] = useState(true);

  // Shopping list selection
  const [shoppingLists, setShoppingLists] = useState<ShoppingList[]>([]);
  const [selectedList, setSelectedList] = useState<ShoppingList | null>(null);
  const [listsLoading, setListsLoading] = useState(false);
  const [showCompletedLists, setShowCompletedLists] = useState(false);

  // Shopping list items preview
  const [previewItems, setPreviewItems] = useState<ShoppingListGrocery[]>([]);
  const [previewListName, setPreviewListName] = useState('');
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  // Active shopping
  const [groceries, setGroceries] = useState<ShoppingListGrocery[]>([]);
  const [prices, setPrices] = useState<Map<number, Price>>(new Map());
  const [groceriesLoading, setGroceriesLoading] = useState(false);

  // Modals
  const [missingPricesAlertOpen, setMissingPricesAlertOpen] = useState(false);
  const [confirmFinishOpen, setConfirmFinishOpen] = useState(false);
  const [finishWithIncomplete, setFinishWithIncomplete] = useState(false);
  const [priceModalOpen, setPriceModalOpen] = useState(false);
  const [selectedGroceryForPrice, setSelectedGroceryForPrice] = useState<ShoppingListGrocery | null>(null);
  const [partialPickModalOpen, setPartialPickModalOpen] = useState(false);
  const [selectedGroceryForPick, setSelectedGroceryForPick] = useState<ShoppingListGrocery | null>(null);
  const [unpricedInfoOpen, setUnpricedInfoOpen] = useState(false);

  // Add New Grocery modal
  const [addGroceryModalOpen, setAddGroceryModalOpen] = useState(false);

  // Receipt scan modal
  const [receiptScanModalOpen, setReceiptScanModalOpen] = useState(false);

  // Remove grocery confirmation
  const [removeGroceryDialogOpen, setRemoveGroceryDialogOpen] = useState(false);
  const [groceryToRemove, setGroceryToRemove] = useState<ShoppingListGrocery | null>(null);
  const [removeLoading, setRemoveLoading] = useState(false);

  // Remove sale confirmation
  const [removeSaleDialogOpen, setRemoveSaleDialogOpen] = useState(false);
  const [saleToRemove, setSaleToRemove] = useState<{ item: ShoppingListGrocery; price: Price } | null>(null);
  const [removeSaleLoading, setRemoveSaleLoading] = useState(false);
  const [fallbackPrice, setFallbackPrice] = useState<string | null>(null);
  const [fallbackPriceLoading, setFallbackPriceLoading] = useState(false);

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Pause shopping modal
  const [pauseShoppingModalOpen, setPauseShoppingModalOpen] = useState(false);

  // Paused shopping trips modal
  const [pausedTripsModalOpen, setPausedTripsModalOpen] = useState(false);
  const [pausedListsCount, setPausedListsCount] = useState(0);

  // Create new list modal (for Step 2)
  const [createListModalOpen, setCreateListModalOpen] = useState(false);

  // Switch store confirmation modal (for paused list from different store)
  const [switchStoreModalOpen, setSwitchStoreModalOpen] = useState(false);
  const [listToSwitchStore, setListToSwitchStore] = useState<ShoppingList | null>(null);

  // Switch store while shopping modal
  const [switchStoreWhileShoppingOpen, setSwitchStoreWhileShoppingOpen] = useState(false);
  const [switchStoreSelectedStore, setSwitchStoreSelectedStore] = useState<Store | null>(null);
  const [switchStoreLoading, setSwitchStoreLoading] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const apiUrl = process.env.REACT_APP_API_URL;
  const tutorialContext = useContext(TutorialContext);

  // Trigger the appropriate tutorial based on the active wizard step
  useEffect(() => {
    if (!tutorialContext) return;

    const tutorialKey = activeStep === 0
      ? 'going_shopping_store' as const
      : activeStep === 1
        ? 'going_shopping_list' as const
        : 'going_shopping_shop' as const;

    if (!tutorialContext.shouldShowTour(tutorialKey)) return;

    // For step 0, wait for stores to load
    if (activeStep === 0 && (storesLoading || stores.length === 0)) return;
    // For step 1, wait for lists to load
    if (activeStep === 1 && listsLoading) return;
    // For step 2, wait for groceries to load
    if (activeStep === 2 && groceriesLoading) return;

    const timer = setTimeout(() => {
      tutorialContext.startTour(tutorialKey);
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, storesLoading, stores.length, listsLoading, groceriesLoading]);

  // Fetch stores and paused lists count on mount
  useEffect(() => {
    const fetchStores = async () => {
      try {
        const data = await apiFetch(`${apiUrl}/api/shops/`);
        setStores(data);
      } catch (err: any) {
        setError(err.message || 'Failed to load stores.');
      } finally {
        setStoresLoading(false);
      }
    };
    const fetchPausedCount = async () => {
      try {
        const data = await apiFetch(`${apiUrl}/api/shopping-lists/`);
        const pausedCount = (data as ShoppingList[]).filter(
          (list) => list.status === 'paused'
        ).length;
        setPausedListsCount(pausedCount);
      } catch (err) {
        // Silently fail - paused count is not critical
      }
    };
    fetchStores();
    fetchPausedCount();
  }, [apiUrl]);

  // Restore shopping session if active
  useEffect(() => {
    // If no active shopping mode, no need to restore
    if (!shoppingMode.isActive) {
      setRestoringSession(false);
      return;
    }

    // Wait for stores to load before attempting restore
    if (storesLoading || stores.length === 0) {
      return; // Keep restoringSession true while waiting
    }

    const restoreSession = async () => {
      try {
        // Find the store from the saved state
        const store = stores.find((s) => s.id === shoppingMode.storeId);
        if (!store) {
          // Store not found, clear shopping mode
          endShopping();
          setRestoringSession(false);
          return;
        }

        // Set the store which will trigger list fetch
        setSelectedStore(store);

        // Fetch the shopping list
        const lists = await apiFetch(`${apiUrl}/api/shopping-lists/`);
        setShoppingLists(lists);

        const list = lists.find((l: ShoppingList) => l.id === shoppingMode.listId);
        if (!list) {
          // List not found, clear shopping mode
          endShopping();
          setRestoringSession(false);
          return;
        }

        // Restore the session
        setSelectedList(list);
        setActiveStep(2);
      } catch (err: any) {
        console.error('Failed to restore shopping session:', err);
        endShopping();
      } finally {
        setRestoringSession(false);
      }
    };

    restoreSession();
  }, [shoppingMode.isActive, shoppingMode.storeId, shoppingMode.listId, stores, storesLoading, apiUrl, endShopping]);

  // Handle navigation state to go directly to active list (when clicking Shop from step 1 or 2)
  useEffect(() => {
    const state = location.state as { goToActiveList?: boolean } | null;
    if (state?.goToActiveList && shoppingMode.isActive && !restoringSession && stores.length > 0) {
      // Find the store and list from shopping mode
      const store = stores.find((s) => s.id === shoppingMode.storeId);
      if (store && shoppingMode.listId) {
        setSelectedStore(store);
        // Fetch the list and go to step 3
        const goToActiveStep = async () => {
          try {
            const lists = await apiFetch(`${apiUrl}/api/shopping-lists/`);
            setShoppingLists(lists);
            const list = lists.find((l: ShoppingList) => l.id === shoppingMode.listId);
            if (list) {
              setSelectedList(list);
              setActiveStep(2);
            }
          } catch (err) {
            console.error('Failed to navigate to active list:', err);
          }
        };
        goToActiveStep();
      }
      // Clear the navigation state to prevent re-triggering
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, shoppingMode.isActive, shoppingMode.storeId, shoppingMode.listId, stores, restoringSession, apiUrl, navigate, location.pathname]);

  // Fetch shopping lists when store is selected
  useEffect(() => {
    if (!selectedStore) return;

    const fetchLists = async () => {
      setListsLoading(true);
      try {
        const data = await apiFetch(`${apiUrl}/api/shopping-lists/`);
        setShoppingLists(data);
      } catch (err: any) {
        setError(err.message || 'Failed to load shopping lists.');
      } finally {
        setListsLoading(false);
      }
    };
    fetchLists();
  }, [apiUrl, selectedStore]);

  // Fetch groceries and prices when list is selected
  useEffect(() => {
    if (!selectedList || !selectedStore) return;

    const fetchGroceriesAndPrices = async () => {
      setGroceriesLoading(true);
      try {
        const [groceriesData, pricesData] = await Promise.all([
          apiFetch(`${apiUrl}/api/shopping-lists/${selectedList.id}/groceries/`),
          apiFetch(`${apiUrl}/api/shops/${selectedStore.id}/prices/`),
        ]);
        setGroceries(groceriesData);

        // Build price map by grocery ID
        const priceMap = new Map<number, Price>();
        pricesData.forEach((p: Price) => {
          priceMap.set(p.grocery, p);
        });
        setPrices(priceMap);
      } catch (err: any) {
        setError(err.message || 'Failed to load shopping data.');
      } finally {
        setGroceriesLoading(false);
      }
    };
    fetchGroceriesAndPrices();
  }, [apiUrl, selectedList, selectedStore]);

  // Handle store selection
  const handleStoreSelect = (store: Store) => {
    setSelectedStore(store);
    setActiveStep(1);
  };

  // Handle preview items click
  const handlePreviewClick = async (e: React.MouseEvent, list: ShoppingList) => {
    e.stopPropagation();
    setPreviewListName(list.name);
    try {
      const items = await apiFetch(`${apiUrl}/api/shopping-lists/${list.id}/groceries/`);
      setPreviewItems(items);
      setPreviewModalOpen(true);
    } catch (err: any) {
      setError(err.message || 'Failed to load items.');
    }
  };

  // Handle list selection
  const handleListSelect = async (list: ShoppingList) => {
    if (!selectedStore) return;

    // Check if this is a paused list at a different store
    if (list.status === 'paused' && list.shop && list.shop !== selectedStore.id) {
      // Show switch store confirmation
      setListToSwitchStore(list);
      setSwitchStoreModalOpen(true);
      return;
    }

    // For active lists at the same store, or not_started lists, proceed directly
    await activateAndSelectList(list);
  };

  // Activate list and start shopping
  const activateAndSelectList = async (list: ShoppingList) => {
    if (!selectedStore) return;

    try {
      // If list is not already active at this store, update its status
      if (list.status !== 'active' || list.shop !== selectedStore.id) {
        await apiFetch(`${apiUrl}/api/shopping-lists/${list.id}/update-status/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'active', shop_id: selectedStore.id }),
        });
      }

      setSelectedList(list);
      setActiveStep(2);
      startShopping(selectedStore.id, selectedStore.name, list.id, list.name);
    } catch (err: any) {
      setError(err.message || 'Failed to activate shopping list.');
    }
  };

  // Handle confirming store switch for paused list
  const handleConfirmSwitchStore = async () => {
    if (listToSwitchStore) {
      await activateAndSelectList(listToSwitchStore);
    }
    setSwitchStoreModalOpen(false);
    setListToSwitchStore(null);
  };

  // Handle new list created from modal
  const handleNewListCreated = async (newList: ShoppingList) => {
    // Add the new list to our local state
    setShoppingLists((prev) => [newList, ...prev]);
    setCreateListModalOpen(false);

    // Activate and start shopping with the new list
    await activateAndSelectList(newList);
  };

  // Handle selecting a paused trip from the modal
  const handleSelectPausedTrip = async (list: ShoppingList) => {
    if (!list.shop) return;

    // Find the store for this list
    const store = stores.find((s) => s.id === list.shop);
    if (!store) {
      setError('Store not found for this shopping list.');
      return;
    }

    try {
      // Activate the list
      await apiFetch(`${apiUrl}/api/shopping-lists/${list.id}/update-status/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'active' }),
      });

      setSelectedStore(store);
      setSelectedList(list);
      setActiveStep(2);
      startShopping(store.id, store.name, list.id, list.name);
    } catch (err: any) {
      setError(err.message || 'Failed to resume shopping.');
    }
  };

  // Handle checkbox click - show partial pick modal for quantity > 1
  const handleCheckboxClick = (item: ShoppingListGrocery) => {
    if (item.quantity > 1) {
      setSelectedGroceryForPick(item);
      setPartialPickModalOpen(true);
    } else {
      // Single item, just toggle
      handlePickItem(item, item.picked === 1 ? 0 : 1);
    }
  };

  // Handle picking an item
  const handlePickItem = async (item: ShoppingListGrocery, picked: number) => {
    const newPicked = Math.min(Math.max(0, picked), item.quantity);
    try {
      const updated = await apiFetch(`${apiUrl}/api/shopping-lists/${selectedList!.id}/groceries/${item.id}/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ picked: newPicked }),
      });
      setGroceries((prev) =>
        prev.map((g) => (g.id === updated.id ? updated : g))
      );
    } catch (err: any) {
      setError(err.message || 'Failed to update item.');
    }
  };

  // Handle partial pick confirm
  const handlePartialPickConfirm = (picked: number) => {
    if (selectedGroceryForPick) {
      handlePickItem(selectedGroceryForPick, picked);
    }
  };

  // Handle entering price
  const handleEnterPriceClick = (item: ShoppingListGrocery) => {
    setSelectedGroceryForPrice(item);
    setPriceModalOpen(true);
  };

  // Handle price saved
  const handlePriceSaved = (savedPrice: Price) => {
    setPrices((prev) => new Map(prev).set(savedPrice.grocery, savedPrice));
    setSuccessMessage('Price saved successfully.');
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  // Handle add grocery while shopping success
  const handleAddGrocerySuccess = async (result: AddGroceryResult) => {
    // Trigger haptic feedback
    haptics.success();

    // Build snackbar message based on what happened (skip if price was skipped)
    if (result.price > 0) {
      let message = '';
      if (result.wasNewGrocery && result.wasAddedToList) {
        message = `Added new grocery, ${result.grocery.name}, to your database and to your shopping list ($${result.price.toFixed(2)})`;
      } else if (result.wasAddedToList) {
        message = `Added ${result.grocery.name} ($${result.price.toFixed(2)}) to your shopping list`;
      } else {
        message = `Added price $${result.price.toFixed(2)}`;
      }

      setSnackbarMessage(message);
      setSnackbarOpen(true);
    }

    // Refresh groceries and prices
    if (selectedList && selectedStore) {
      try {
        const [groceriesData, pricesData] = await Promise.all([
          apiFetch(`${apiUrl}/api/shopping-lists/${selectedList.id}/groceries/`),
          apiFetch(`${apiUrl}/api/shops/${selectedStore.id}/prices/`),
        ]);
        setGroceries(groceriesData);

        const priceMap = new Map<number, Price>();
        pricesData.forEach((p: Price) => {
          priceMap.set(p.grocery, p);
        });
        setPrices(priceMap);
      } catch (err: any) {
        setError(err.message || 'Failed to refresh data.');
      }
    }
  };

  // Handle remove grocery click - show confirmation
  const handleRemoveGroceryClick = (item: ShoppingListGrocery) => {
    setGroceryToRemove(item);
    setRemoveGroceryDialogOpen(true);
  };

  // Confirm remove grocery
  const handleConfirmRemoveGrocery = async () => {
    if (!groceryToRemove || !selectedList) return;

    setRemoveLoading(true);
    try {
      await apiFetch(`${apiUrl}/api/shopping-lists/${selectedList.id}/groceries/${groceryToRemove.id}/`, {
        method: 'DELETE',
      });

      // Remove from local state
      setGroceries((prev) => prev.filter((g) => g.id !== groceryToRemove.id));

      // Show success message
      setSnackbarMessage(`Removed ${groceryToRemove.grocery_details.name} from list`);
      setSnackbarOpen(true);

      // Trigger haptic feedback
      haptics.success();
    } catch (err: any) {
      setError(err.message || 'Failed to remove item.');
    } finally {
      setRemoveLoading(false);
      setRemoveGroceryDialogOpen(false);
      setGroceryToRemove(null);
    }
  };

  // Handle remove sale click
  const handleRemoveSaleClick = async (item: ShoppingListGrocery, price: Price) => {
    setSaleToRemove({ item, price });
    setFallbackPrice(null);
    setRemoveSaleDialogOpen(true);

    // If no price_before_discount, try to fetch price history to find a fallback
    const priceBeforeDiscount = price.price_before_discount;
    if ((!priceBeforeDiscount || priceBeforeDiscount === '') && selectedStore) {
      setFallbackPriceLoading(true);
      try {
        // Fetch price details for this specific grocery (same endpoint as ViewGroceryPrices uses)
        const groceryId = item.grocery;
        const priceDetails = await apiFetch(`${apiUrl}/api/groceries/${groceryId}/price-details/`);

        // Find the store matching our current store
        const storeData = priceDetails.stores?.find(
          (s: { shop: { id: number } }) => s.shop.id === selectedStore.id
        );

        if (!storeData || !storeData.price_history || storeData.price_history.length === 0) {
          return;
        }

        // Price history is already sorted by date descending from the API
        const priceHistory = storeData.price_history;
        const currentSaleDate = new Date(price.created_at || 0).getTime();

        // Find the latest non-sale price that is OLDER than the current sale
        for (const p of priceHistory) {
          const pDate = new Date(p.created_at || 0).getTime();

          // Must be older than current sale
          if (pDate >= currentSaleDate) continue;

          // Check for non-sale price
          if (!p.is_discounted) {
            setFallbackPrice(p.price);
            break;
          }
        }
      } catch (err) {
        // Non-critical, just means we can't find a fallback
      } finally {
        setFallbackPriceLoading(false);
      }
    }
  };

  // Get the original price for removing sale
  const getRemoveSalePrice = (): string | null => {
    if (!saleToRemove) return null;
    // Check for price_before_discount first (handle empty string as falsy)
    const priceBeforeDiscount = saleToRemove.price.price_before_discount;
    if (priceBeforeDiscount && priceBeforeDiscount !== '') {
      return priceBeforeDiscount;
    }
    return fallbackPrice;
  };

  // Confirm remove sale
  const handleConfirmRemoveSale = async () => {
    if (!saleToRemove || !selectedStore) return;

    // Determine the original price
    const originalPrice = getRemoveSalePrice();
    if (!originalPrice) {
      setError('Could not determine original price. Please update the price manually.');
      setRemoveSaleDialogOpen(false);
      setSaleToRemove(null);
      return;
    }

    setRemoveSaleLoading(true);
    try {
      const newPrice = await apiFetch(`${apiUrl}/api/prices/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          grocery: saleToRemove.item.grocery,
          shop: selectedStore.id,
          price: originalPrice,
          is_discounted: false,
        }),
      });

      // Update local prices state
      setPrices((prev) => {
        const updated = new Map(prev);
        updated.set(saleToRemove.item.grocery, newPrice);
        return updated;
      });

      setSnackbarMessage(`Sale removed. Price restored to $${parseFloat(originalPrice).toFixed(2)}`);
      setSnackbarOpen(true);
      haptics.success();
    } catch (err: any) {
      setError(err.message || 'Failed to remove sale.');
    } finally {
      setRemoveSaleLoading(false);
      setRemoveSaleDialogOpen(false);
      setSaleToRemove(null);
      setFallbackPrice(null);
    }
  };

  // Handle completing shopping (navigate to list and end shopping mode)
  const handleCompleteShopping = () => {
    endShopping();
    navigate(`/shopping-lists/${selectedList?.id}`);
  };

  // Handle pausing shopping
  const handlePauseShopping = () => {
    setPauseShoppingModalOpen(true);
  };

  const handleConfirmPauseShopping = async () => {
    if (!selectedList) {
      pauseShopping();
      setPauseShoppingModalOpen(false);
      navigate('/');
      return;
    }

    try {
      // Update status to paused on the server
      await apiFetch(`${apiUrl}/api/shopping-lists/${selectedList.id}/update-status/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'paused' }),
      });

      pauseShopping();
      setPauseShoppingModalOpen(false);
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Failed to pause shopping.');
      setPauseShoppingModalOpen(false);
    }
  };

  // Handle finish shopping - show confirmation
  const handleFinishShopping = () => {
    if (!selectedList || selectedList.completed) return;

    // Check if all picked items have prices
    if (hasUnpricedPicked) {
      setMissingPricesAlertOpen(true);
      return;
    }

    // Check if all items are picked (at least partially)
    const allItemsPickedLocal = groceries.every((item) => item.picked > 0);
    setFinishWithIncomplete(!allItemsPickedLocal);

    // Show confirmation dialog
    setConfirmFinishOpen(true);
  };

  // Separate function to complete the list
  const completeList = async () => {
    if (!selectedList) return;

    try {
      const updated = await apiFetch(`${apiUrl}/api/shopping-lists/${selectedList.id}/toggle-complete/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shop_id: selectedStore?.id }),
      });
      setSelectedList(updated);
      endShopping(); // Clear active shopping state
      analytics.trackEvent('shopping_list_complete', 'conversion', {
        items_count: groceries.length,
        store_name: selectedStore?.name,
      });
      setSuccessMessage('Shopping list completed!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to complete list.');
    }
  };

  // Handle confirm finish shopping
  const handleConfirmFinish = async () => {
    setConfirmFinishOpen(false);
    await completeList();
  };

  // Handle switch store while shopping
  const handleSwitchStoreClick = () => {
    setSwitchStoreSelectedStore(null);
    setSwitchStoreWhileShoppingOpen(true);
  };

  const handleSwitchStoreConfirm = async () => {
    if (!selectedList || !switchStoreSelectedStore) return;

    setSwitchStoreLoading(true);
    try {
      const response = await apiFetch(`${apiUrl}/api/shopping-lists/${selectedList.id}/switch-store/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shop_id: switchStoreSelectedStore.id }),
      });

      if (response.mode === 'simple_switch') {
        // Mode 1: Simple switch - just update the current store and prices
        setSelectedStore(switchStoreSelectedStore);
        setSelectedList(response.list);
        // Refetch prices for new store
        const pricesData = await apiFetch(`${apiUrl}/api/shops/${switchStoreSelectedStore.id}/prices/`);
        const priceMap = new Map<number, Price>();
        pricesData.forEach((p: Price) => {
          priceMap.set(p.grocery, p);
        });
        setPrices(priceMap);
        setSuccessMessage(`Switched to ${switchStoreSelectedStore.name}`);
      } else if (response.mode === 'move_items') {
        // Mode 2: Items moved to new list - update to new list
        setSelectedStore(switchStoreSelectedStore);
        setSelectedList(response.new_list);
        // Refetch groceries and prices for new list
        const [groceriesData, pricesData] = await Promise.all([
          apiFetch(`${apiUrl}/api/shopping-lists/${response.new_list.id}/groceries/`),
          apiFetch(`${apiUrl}/api/shops/${switchStoreSelectedStore.id}/prices/`),
        ]);
        setGroceries(groceriesData);
        const priceMap = new Map<number, Price>();
        pricesData.forEach((p: Price) => {
          priceMap.set(p.grocery, p);
        });
        setPrices(priceMap);
        // Update shopping mode context
        startShopping(switchStoreSelectedStore.id, switchStoreSelectedStore.name, response.new_list.id, response.new_list.name);
        setSuccessMessage(`${response.items_moved} item(s) moved to new list at ${switchStoreSelectedStore.name}`);
      }

      setSwitchStoreWhileShoppingOpen(false);
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err: any) {
      setError(err.message || 'Failed to switch store.');
    } finally {
      setSwitchStoreLoading(false);
    }
  };

  // Calculate totals - memoized to prevent recalculation on every render
  const totals = useMemo(() => {
    let pickedTotal = 0;
    let estimatedTotal = 0;
    let allPriced = true;
    let hasUnpricedPicked = false;
    let itemsSelected = 0;
    let unpricedCount = 0;
    let fullyUnpickedCount = 0; // Items with picked = 0
    let fullyPickedCount = 0; // Items with picked = quantity
    const totalItems = groceries.length;

    groceries.forEach((item) => {
      const price = prices.get(item.grocery);
      const priceValue = price ? parseFloat(price.price) : 0;

      if (item.picked > 0) {
        itemsSelected++;
        if (item.picked >= item.quantity) {
          fullyPickedCount++;
        }
      } else {
        fullyUnpickedCount++;
      }

      if (price) {
        estimatedTotal += priceValue * item.quantity;
        pickedTotal += priceValue * item.picked;
      } else {
        allPriced = false;
        unpricedCount++;
        if (item.picked > 0) {
          hasUnpricedPicked = true;
        }
      }
    });

    const allItemsPicked = totalItems > 0 && fullyPickedCount === totalItems;

    return { pickedTotal, estimatedTotal, allPriced, hasUnpricedPicked, itemsSelected, totalItems, unpricedCount, fullyUnpickedCount, fullyPickedCount, allItemsPicked };
  }, [groceries, prices]);

  // Format date
  const formatDate = (dateString?: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Go back handler
  const handleBack = () => {
    if (activeStep === 0) {
      navigate('/');
    } else if (activeStep === 1) {
      setSelectedStore(null);
      setActiveStep(0);
    } else {
      // Refetch shopping lists to get updated completion status
      const refetchLists = async () => {
        try {
          const data = await apiFetch(`${apiUrl}/api/shopping-lists/`);
          setShoppingLists(data);
        } catch (err: any) {
          setError(err.message || 'Failed to load shopping lists.');
        }
      };
      refetchLists();
      setSelectedList(null);
      setActiveStep(1);
    }
  };

  // Filter shopping lists based on status and completed toggle - memoized
  const { filteredLists, completedListsCount } = useMemo(() => {
    const filtered = shoppingLists.filter((list) => {
      // Show completed lists if toggle is on
      if (list.status === 'completed' || list.completed) {
        return showCompletedLists;
      }

      // Show not_started lists (with no items picked)
      if (list.status === 'not_started') {
        return true;
      }

      // Show paused lists only if they have no items picked
      if (list.status === 'paused') {
        return (list.picked_count ?? 0) === 0;
      }

      // Show active lists only if they're for the current store
      if (list.status === 'active' && selectedStore) {
        return list.shop === selectedStore.id;
      }

      return true;
    });

    const completedCount = shoppingLists.filter((list) => list.status === 'completed' || list.completed).length;

    return { filteredLists: filtered, completedListsCount: completedCount };
  }, [shoppingLists, showCompletedLists, selectedStore]);

  const { pickedTotal, estimatedTotal, allPriced, hasUnpricedPicked, itemsSelected, totalItems, unpricedCount, fullyUnpickedCount, allItemsPicked } = totals;

  // Memoize props for ReceiptScanModal to prevent unnecessary re-renders
  const receiptScanGroceryItems = useMemo(() =>
    groceries.map(g => ({
      id: g.grocery,
      name: g.grocery_details.name,
      brand: g.grocery_details.brand,
      quantity: g.quantity,
    })), [groceries]);

  const currentPricesMap = useMemo(() =>
    new Map(Array.from(prices.entries()).map(([groceryId, price]) => [groceryId, price.price])),
    [prices]);

  // Show loading while restoring session
  if (shoppingMode.isActive && (restoringSession || storesLoading)) {
    return (
      <Container maxWidth="md" sx={{ mt: 5 }}>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            Restoring your shopping session...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 5 }}>
      {/* Header */}
      <Box className="title-row" sx={{ mb: 2 }}>
        <IconButton onClick={handleBack} aria-label="go back">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1" className="page-title">Going Shopping</Typography>
        {activeStep === 0 && pausedListsCount > 0 && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<PauseCircleOutlineIcon />}
            onClick={() => setPausedTripsModalOpen(true)}
            sx={{ ml: 'auto' }}
            data-tutorial="shopping-paused-trips"
          >
            Paused ({pausedListsCount})
          </Button>
        )}
      </Box>

      <Box data-tutorial="shopping-step-wizard">
        <StepWizard steps={steps} activeStep={activeStep} sx={{ mb: 4 }} />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}

      {/* Step 1: Select Store */}
      {activeStep === 0 && (
        <>
          <Typography variant="h6" gutterBottom>Select the store you're shopping at:</Typography>
          {storesLoading ? (
            <CircularProgress sx={{ display: 'block', margin: '20px auto' }} />
          ) : stores.length === 0 ? (
            <Alert severity="info">No stores found. Add stores first.</Alert>
          ) : (
            <List data-tutorial="shopping-store-list">
              {stores.map((store) => (
                <ListItemButton key={store.id} onClick={() => handleStoreSelect(store)}>
                  <ListItemAvatar>
                    <Avatar
                      src={store.icon_url || `${process.env.REACT_APP_IMAGES_URL}/placeholder-store.svg`}
                      variant="rounded"
                      sx={{ width: 40, height: 40 }}
                    />
                  </ListItemAvatar>
                  <ListItemText primary={store.name} secondary={store.address_line1} />
                </ListItemButton>
              ))}
            </List>
          )}
        </>
      )}

      {/* Step 2: Select Shopping List */}
      {activeStep === 1 && (
        <>
          <Box sx={{ mb: 2 }} data-tutorial="shopping-completed-toggle">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="h6">
                Select a shopping list for {selectedStore?.name}:
              </Typography>
              {!isMobile && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={showCompletedLists}
                      onChange={(e) => setShowCompletedLists(e.target.checked)}
                    />
                  }
                  label={`Show completed (${completedListsCount})`}
                />
              )}
            </Box>
            {isMobile && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={showCompletedLists}
                      onChange={(e) => setShowCompletedLists(e.target.checked)}
                    />
                  }
                  label={`Show completed (${completedListsCount})`}
                />
              </Box>
            )}
          </Box>
          {/* Create New List Button */}
          <Button
            data-tutorial="shopping-create-list"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setCreateListModalOpen(true)}
            fullWidth
            sx={{ mb: 2 }}
          >
            Create New List
          </Button>

          {listsLoading ? (
            <CircularProgress sx={{ display: 'block', margin: '20px auto' }} />
          ) : filteredLists.length === 0 ? (
            <Alert severity="info">
              {showCompletedLists
                ? 'No shopping lists found. Create a new list above to start shopping!'
                : 'No open shopping lists. Create a new list above or toggle "Show completed".'}
            </Alert>
          ) : (
            <List data-tutorial="shopping-list-selection">
              {filteredLists.map((list) => {
                const isActiveHere = list.status === 'active' && list.shop === selectedStore?.id;
                const getStatusChip = () => {
                  if (list.status === 'completed' || list.completed) {
                    return <Chip label="Done" size="small" color="primary" variant="filled" sx={{ fontSize: '0.7rem', height: '20px', lineHeight: '18px', minWidth: 40 }} />;
                  }
                  if (list.status === 'active') {
                    return <Chip label="Active" size="small" color="success" variant="filled" sx={{ fontSize: '0.7rem', height: '20px', lineHeight: '18px', minWidth: 50 }} />;
                  }
                  if (list.status === 'paused') {
                    return <Chip label="Paused" size="small" color="warning" variant="outlined" sx={{ fontSize: '0.7rem', height: '20px', lineHeight: '18px', minWidth: 50 }} />;
                  }
                  return <Chip label="Open" size="small" color="primary" variant="outlined" sx={{ fontSize: '0.7rem', height: '20px', lineHeight: '18px', minWidth: 40 }} />;
                };

                return (
                  <ListItemButton
                    key={list.id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      ...(isActiveHere && {
                        bgcolor: 'action.selected',
                        borderLeft: '3px solid',
                        borderColor: 'success.main',
                      }),
                    }}
                  >
                    <ListItemAvatar sx={{ alignSelf: 'flex-start', mt: 1, mr: 1 }}>
                      {getStatusChip()}
                    </ListItemAvatar>
                    <ListItemText
                      primary={list.name}
                      secondary={`${formatDate(list.created_at)}${list.description ? ` | ${list.description}` : ''}`}
                      onClick={() => handleListSelect(list)}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {(list.item_count_all ?? 0) === 0 ? (
                        <Chip
                          label="Empty"
                          size="small"
                          variant="outlined"
                          color="warning"
                        />
                      ) : (
                        <Chip
                          label={`${list.item_count_all} ${list.item_count_all === 1 ? 'item' : 'items'}`}
                          size="small"
                          onClick={(e) => handlePreviewClick(e, list)}
                          sx={{ cursor: 'pointer' }}
                        />
                      )}
                      {!isMobile && (
                        <Button size="small" variant={isActiveHere ? 'contained' : 'outlined'} onClick={() => handleListSelect(list)}>
                          {isActiveHere ? 'Continue' : 'Select'}
                        </Button>
                      )}
                    </Box>
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </>
      )}

      {/* Step 3: Active Shopping */}
      {activeStep === 2 && selectedList && selectedStore && (
        <>
          {/* Totals Header */}
          <Box data-tutorial="shopping-totals" sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              {/* Items Selected - Left */}
              <Box>
                <Typography variant="body2" color="text.secondary">Items Selected</Typography>
                <Typography variant="h5">{itemsSelected}/{totalItems}</Typography>
              </Box>

              {/* Current Total - Center */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">Current Total</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <Typography variant="h5">${pickedTotal.toFixed(2)}</Typography>
                  {hasUnpricedPicked && (
                    <WarningAmberIcon color="warning" titleAccess="Some picked items have no price" />
                  )}
                </Box>
              </Box>

              {/* Estimated Total - Right */}
              <Box sx={{ textAlign: 'right', minWidth: isMobile ? 'auto' : 120 }}>
                <Typography variant="body2" color="text.secondary">Estimated Total</Typography>
                {allPriced ? (
                  <Typography variant="h5">${estimatedTotal.toFixed(2)}</Typography>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: 0.5,
                      cursor: 'pointer',
                    }}
                    onClick={() => setUnpricedInfoOpen(true)}
                  >
                    <Typography variant="h6" color="text.secondary">
                      ${estimatedTotal.toFixed(2)}*
                    </Typography>
                    <WarningAmberIcon color="warning" fontSize="small" />
                  </Box>
                )}
              </Box>
            </Box>

            {/* Desktop buttons - 2x2 grid */}
            {!isMobile && !selectedList.completed && (
              <Box data-tutorial="shopping-actions" sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2, maxWidth: 500, mx: 'auto' }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleSwitchStoreClick}
                    startIcon={<SwapHorizIcon />}
                    disabled={allItemsPicked}
                    sx={{ flex: 1 }}
                  >
                    Switch Store
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setReceiptScanModalOpen(true)}
                    startIcon={<ReceiptLongIcon />}
                    sx={{ flex: 1 }}
                  >
                    Scan Receipt
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={handlePauseShopping}
                    startIcon={<PauseCircleOutlineIcon />}
                    sx={{ flex: 1, color: 'text.secondary' }}
                  >
                    Pause
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleFinishShopping}
                    startIcon={<CheckCircleIcon />}
                    sx={{ flex: 1 }}
                  >
                    Finish Shopping
                  </Button>
                </Box>
              </Box>
            )}

            {/* Done - Go to List button (desktop) */}
            {!isMobile && selectedList.completed && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<ShoppingCartCheckoutIcon />}
                  onClick={handleCompleteShopping}
                  sx={{ width: '50%' }}
                >
                  Done — Go to List
                </Button>
              </Box>
            )}

            {/* Mobile buttons - 2x2 grid */}
            {isMobile && !selectedList.completed && (
              <Box data-tutorial="shopping-actions" sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleSwitchStoreClick}
                    startIcon={<SwapHorizIcon />}
                    disabled={allItemsPicked}
                    sx={{ flex: 1 }}
                  >
                    Switch Store
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setReceiptScanModalOpen(true)}
                    startIcon={<ReceiptLongIcon />}
                    sx={{ flex: 1 }}
                  >
                    Scan Receipt
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={handlePauseShopping}
                    startIcon={<PauseCircleOutlineIcon />}
                    sx={{ flex: 1, color: 'text.secondary' }}
                  >
                    Pause
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleFinishShopping}
                    startIcon={<CheckCircleIcon />}
                    sx={{ flex: 1 }}
                  >
                    Finish
                  </Button>
                </Box>
              </Box>
            )}

            {/* Done - Go to List button (mobile) */}
            {isMobile && selectedList.completed && (
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  startIcon={<ShoppingCartCheckoutIcon />}
                  onClick={handleCompleteShopping}
                >
                  Done — Go to List
                </Button>
              </Box>
            )}
          </Box>

          <Typography variant="h6" sx={{ mb: 1 }}>
            Shopping at{' '}
            <RouterLink
              to={`/stores/${selectedStore.id}`}
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              {selectedStore.name}
            </RouterLink>
            :{' '}
            <RouterLink
              to={`/shopping-lists/${selectedList.id}`}
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              {selectedList.name}
            </RouterLink>
          </Typography>

          <Divider sx={{ mb: 2 }} />

          {groceriesLoading ? (
            <CircularProgress sx={{ display: 'block', margin: '20px auto' }} />
          ) : groceries.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
              <Typography variant="h6" sx={{ color: 'text.disabled', mb: 2 }}>
                Your shopping list is empty
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.disabled', mb: 1 }}>
                Build your list as you shop!
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                Tap the <AddShoppingCartIcon sx={{ fontSize: 18, verticalAlign: 'middle', mx: 0.5 }} /> button in the bottom right corner to add groceries to your list.
              </Typography>
            </Box>
          ) : (
            <List data-tutorial="shopping-items">
              {groceries.map((item) => (
                <ShoppingListItem
                  key={item.id}
                  item={item}
                  price={prices.get(item.grocery)}
                  isCompleted={selectedList.completed}
                  isMobile={isMobile}
                  onCheckboxClick={handleCheckboxClick}
                  onEnterPriceClick={handleEnterPriceClick}
                  onRemoveClick={handleRemoveGroceryClick}
                  onRemoveSaleClick={handleRemoveSaleClick}
                />
              ))}
            </List>
          )}

          {/* FAB for adding new grocery while shopping */}
          {!selectedList.completed && (
            <Box
              sx={{
                position: 'fixed',
                bottom: isMobile ? 80 : 80,
                left: 0,
                right: 0,
                display: 'flex',
                justifyContent: 'center',
                pointerEvents: 'none',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  maxWidth: theme.breakpoints.values.md,
                  px: 3,
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <Fab
                  color="primary"
                  aria-label="add new grocery"
                  onClick={() => setAddGroceryModalOpen(true)}
                  sx={{ pointerEvents: 'auto' }}
                >
                  <AddShoppingCartIcon />
                </Fab>
              </Box>
            </Box>
          )}
        </>
      )}

      {/* Preview Items Modal */}
      <ShoppingListItemsPreviewModal
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        listName={previewListName}
        items={previewItems}
      />

      {/* Confirm Finish Shopping Dialog */}
      <Dialog
        open={confirmFinishOpen}
        onClose={() => setConfirmFinishOpen(false)}
      >
        <DialogTitle>{finishWithIncomplete ? 'Finish with Unpicked Items?' : 'Finish Shopping?'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {finishWithIncomplete
              ? 'Not all items have been picked from your shopping list. Are you sure you want to complete shopping? This action cannot be undone, you\'ll need to duplicate the list to continue shopping later.'
              : 'Are you sure you want to complete this shopping list? This action cannot be undone. If you need to continue shopping later, you\'ll need to duplicate the list to create a new one.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmFinishOpen(false)}>
            {finishWithIncomplete ? 'Continue Shopping' : 'Cancel'}
          </Button>
          <Button onClick={handleConfirmFinish} variant="contained" color={finishWithIncomplete ? 'warning' : 'primary'}>
            {finishWithIncomplete ? 'Finish Anyway' : 'Finish Shopping'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enter Price Modal */}
      {selectedGroceryForPrice && (
        <EnterPriceModal
          open={priceModalOpen}
          onClose={() => {
            setPriceModalOpen(false);
            setSelectedGroceryForPrice(null);
          }}
          onSuccess={handlePriceSaved}
          groceryId={selectedGroceryForPrice.grocery}
          groceryName={selectedGroceryForPrice.grocery_details.name}
          shopId={selectedStore!.id}
          existingPrice={prices.get(selectedGroceryForPrice.grocery)}
        />
      )}

      {/* Partial Pick Modal */}
      {selectedGroceryForPick && (
        <PartialPickModal
          open={partialPickModalOpen}
          onClose={() => {
            setPartialPickModalOpen(false);
            setSelectedGroceryForPick(null);
          }}
          onConfirm={handlePartialPickConfirm}
          groceryName={selectedGroceryForPick.grocery_details.name}
          quantity={selectedGroceryForPick.quantity}
          currentPicked={selectedGroceryForPick.picked}
        />
      )}

      {/* Missing Prices Alert */}
      <Dialog
        open={missingPricesAlertOpen}
        onClose={() => setMissingPricesAlertOpen(false)}
      >
        <DialogTitle>Missing Prices</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Some picked items don't have prices set. Please add prices for all picked items before completing the shopping list.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMissingPricesAlertOpen(false)} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unpriced Items Info Dialog */}
      <Dialog
        open={unpricedInfoOpen}
        onClose={() => setUnpricedInfoOpen(false)}
        maxWidth="xs"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          Incomplete Pricing
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {unpricedCount} {unpricedCount === 1 ? 'item doesn\'t have a price yet' : 'items don\'t have prices yet'}.
            The estimated total shown is based only on priced items.
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            Tap the <AttachMoneyIcon fontSize="small" sx={{ verticalAlign: 'middle', mx: 0.5 }} />
            button next to each item to add prices and see an accurate total.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnpricedInfoOpen(false)} variant="contained">
            Got it
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Grocery Confirmation Dialog */}
      <Dialog
        open={removeGroceryDialogOpen}
        onClose={() => {
          if (!removeLoading) {
            setRemoveGroceryDialogOpen(false);
            setGroceryToRemove(null);
          }
        }}
      >
        <DialogTitle>Remove Item?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove <strong>{groceryToRemove?.grocery_details.name}</strong> from this shopping list?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRemoveGroceryDialogOpen(false);
              setGroceryToRemove(null);
            }}
            disabled={removeLoading}
          >
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={handleConfirmRemoveGrocery}
            color="error"
            variant="contained"
            disabled={removeLoading}
          >
            {removeLoading ? <CircularProgress size={24} /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pause Shopping Confirmation Dialog */}
      <Dialog open={pauseShoppingModalOpen} onClose={() => setPauseShoppingModalOpen(false)}>
        <DialogTitle>Pause Shopping?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your shopping session will be paused. You can return to this shopping list at any time to continue where you left off.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPauseShoppingModalOpen(false)}>
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={handleConfirmPauseShopping} variant="contained" color="warning">
            Pause Shopping
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Sale Confirmation Dialog */}
      <Dialog open={removeSaleDialogOpen} onClose={() => setRemoveSaleDialogOpen(false)}>
        <DialogTitle>Remove Sale Price?</DialogTitle>
        <DialogContent>
          {fallbackPriceLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (() => {
            const originalPrice = getRemoveSalePrice();
            if (originalPrice) {
              return (
                <>
                  <DialogContentText>
                    This will restore <strong>{saleToRemove?.item.grocery_details.name}</strong> to its regular price of <strong>${parseFloat(originalPrice).toFixed(2)}</strong>.
                  </DialogContentText>
                  <DialogContentText sx={{ mt: 2 }}>
                    Use this when a sale has ended and you want to update the item to its regular price.
                  </DialogContentText>
                </>
              );
            }
            return (
              <DialogContentText color="warning.main">
                No previous regular price found for <strong>{saleToRemove?.item.grocery_details.name}</strong>. Please use the <strong>Update Price</strong> button to enter the current price manually.
              </DialogContentText>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveSaleDialogOpen(false)} disabled={removeSaleLoading}>
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={handleConfirmRemoveSale}
            color="warning"
            variant="contained"
            disabled={removeSaleLoading || fallbackPriceLoading || !getRemoveSalePrice()}
          >
            {removeSaleLoading ? <CircularProgress size={20} /> : 'Remove Sale'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Grocery While Shopping Modal */}
      {selectedStore && selectedList && (
        <AddGroceryWhileShoppingModal
          open={addGroceryModalOpen}
          onClose={() => setAddGroceryModalOpen(false)}
          onSuccess={handleAddGrocerySuccess}
          shopId={selectedStore.id}
          shopName={selectedStore.name}
          shoppingListId={selectedList.id}
          existingGroceries={groceries}
          prices={prices}
        />
      )}

      {/* Receipt Scan Modal */}
      {selectedStore && selectedList && (
        <ReceiptScanModal
          open={receiptScanModalOpen}
          onClose={() => setReceiptScanModalOpen(false)}
          onPricesUpdated={async () => {
            // Refresh prices after receipt scan
            try {
              const pricesData = await apiFetch(`${apiUrl}/api/shops/${selectedStore.id}/prices/`);
              const priceMap = new Map<number, Price>();
              pricesData.forEach((p: Price) => {
                priceMap.set(p.grocery, p);
              });
              setPrices(priceMap);
              setSnackbarMessage('Prices updated from receipt');
              setSnackbarOpen(true);
            } catch (err: any) {
              console.error('Failed to refresh prices:', err);
            }
          }}
          shopId={selectedStore.id}
          groceryItems={receiptScanGroceryItems}
          currentPrices={currentPricesMap}
        />
      )}

      {/* Paused Shopping Trips Modal */}
      <PausedShoppingTripsModal
        open={pausedTripsModalOpen}
        onClose={() => setPausedTripsModalOpen(false)}
        onSelectTrip={handleSelectPausedTrip}
      />

      {/* Create New Shopping List Modal */}
      <ShoppingListFormModal
        open={createListModalOpen}
        onClose={() => setCreateListModalOpen(false)}
        onSuccess={handleNewListCreated}
      />

      {/* Switch Store Confirmation Dialog (for paused list from different store) */}
      <Dialog open={switchStoreModalOpen} onClose={() => setSwitchStoreModalOpen(false)}>
        <DialogTitle>Switch Store?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This shopping list was paused at <strong>{listToSwitchStore?.shop_name}</strong>.
            Do you want to continue shopping at <strong>{selectedStore?.name}</strong> instead?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSwitchStoreModalOpen(false)}>
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={handleConfirmSwitchStore} variant="contained" color="primary">
            Switch Store
          </Button>
        </DialogActions>
      </Dialog>

      {/* Switch Store While Shopping Modal */}
      <Dialog
        open={switchStoreWhileShoppingOpen}
        onClose={() => !switchStoreLoading && setSwitchStoreWhileShoppingOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SwapHorizIcon />
            Switch Store
          </Box>
        </DialogTitle>
        <DialogContent>
          {itemsSelected === 0 ? (
            // Mode 1: No items picked - simple switch
            <DialogContentText sx={{ mb: 2 }}>
              Select a different store to continue shopping. Your current list will be updated with prices from the new store.
            </DialogContentText>
          ) : (
            // Mode 2: Some items picked - move to new list
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                You have already been shopping and picked <strong>{itemsSelected}</strong> item(s) at <strong>{selectedStore?.name}</strong>.
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                You can switch to another store. This will move the <strong>{fullyUnpickedCount}</strong> unpicked item(s) to a new shopping list at the new store where you can continue shopping.
              </Typography>
              <Typography variant="body2">
                Your existing list will be marked as complete and you can review it in the shopping lists section later.
              </Typography>
            </Alert>
          )}

          <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>
            Select new store:
          </Typography>
          <List sx={{ maxHeight: 300, overflow: 'auto' }}>
            {stores.filter(s => s.id !== selectedStore?.id).map((store) => (
              <ListItemButton
                key={store.id}
                selected={switchStoreSelectedStore?.id === store.id}
                onClick={() => setSwitchStoreSelectedStore(store)}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  border: '1px solid',
                  borderColor: switchStoreSelectedStore?.id === store.id ? 'primary.main' : 'divider',
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    src={store.icon_url || `${process.env.REACT_APP_IMAGES_URL}/placeholder-store.svg`}
                    variant="rounded"
                    sx={{ width: 40, height: 40 }}
                  />
                </ListItemAvatar>
                <ListItemText primary={store.name} secondary={store.address_line1} />
                {switchStoreSelectedStore?.id === store.id && (
                  <StorefrontIcon color="primary" />
                )}
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSwitchStoreWhileShoppingOpen(false)}
            disabled={switchStoreLoading}
          >
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={handleSwitchStoreConfirm}
            variant="contained"
            color="primary"
            disabled={!switchStoreSelectedStore || switchStoreLoading}
            startIcon={switchStoreLoading ? <CircularProgress size={16} /> : <SwapHorizIcon />}
          >
            {itemsSelected === 0 ? 'Switch Store' : 'Move To New List'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for success messages */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ mb: isMobile ? 8 : 0 }}
      />
    </Container>
  );
};

export default GoingShopping;
